import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';
import { environment } from '../config/environment';
import { storage } from '../utils/storage';
import { getUserIdFromToken } from '../utils/jwtUtils';
import { RecordingMeta } from '../services/recordingBufferService';
import { ScribeUploadQueue } from '../services/scribeUploadQueue';
import { jobIsActive, scribeJobService, ScribeJob } from '../services/api/scribeJobService';

type UploadItem = { meta: RecordingMeta; stage: string; error?: string };
interface QueueContext {
  jobs: ScribeJob[];
  uploads: UploadItem[];
  submit: (meta: RecordingMeta) => void;
}
const ScribeQueueContext = createContext<QueueContext>({ jobs: [], uploads: [], submit: () => {} });
export const useScribeQueue = () => useContext(ScribeQueueContext);

function accountScope(): string | null {
  const user = storage.getUser();
  if (!storage.getAccessToken() || !user?.is_medical_officer || !user.clinic_id) return null;
  const doctorId = user.acting_as_doctor_id || getUserIdFromToken(storage.getAccessToken()!);
  return doctorId ? `${doctorId}:${user.clinic_id}` : null;
}

export function ScribeQueueProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const queue = useRef(new ScribeUploadQueue());
  const [jobs, setJobs] = useState<ScribeJob[]>([]);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [dismissed, setDismissed] = useState<number[]>([]);
  const [pollError, setPollError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const scope = useRef<string | null>(accountScope());
  const ownerEpoch = useRef(0);
  const jobRevision = useRef(0);
  const visibleUploads = useRef(uploads);
  visibleUploads.current = uploads;

  const submit = useCallback((meta: RecordingMeta) => {
    const owner = accountScope();
    if (!owner || meta.ownerScope !== owner) throw new Error('Sign in to the original account to resume this recording.');
    const epoch = ownerEpoch.current;
    const stillOwned = () => accountScope() === owner && epoch === ownerEpoch.current;
    const assertOwner = () => {
      if (!stillOwned()) throw new Error('Sign in to the original account to resume this upload.');
    };
    const update = (stage: string, error?: string) => {
      if (!stillOwned()) return;
      setUploads(current => [...current.filter(item => item.meta.recordingId !== meta.recordingId),
        { meta, stage, error }]);
    };
    update('Preparing');
    queue.current.submit(meta, assertOwner, update).then(job => {
      if (!stillOwned()) return;
      jobRevision.current += 1;
      setJobs(current => [job, ...current.filter(item => item.job_id !== job.job_id)]);
      setUploads(current => current.filter(item => item.meta.recordingId !== meta.recordingId));
    }).catch(error => update('Upload needs attention', error instanceof Error ? error.message : 'Upload failed'));
  }, []);

  useEffect(() => {
    if (!environment.SCRIBE_BACKGROUND_ENABLED) return;
    let stopped = false;
    let fetching = false;
    const tick = async () => {
      const owner = accountScope();
      if (scope.current !== owner) {
        ownerEpoch.current += 1;
        queue.current.cancelAll();
        scope.current = owner;
        setJobs([]); setUploads([]); setDismissed([]); setPollError(false);
      }
      if (!owner || document.hidden || fetching) return;
      fetching = true;
      const revision = jobRevision.current;
      try {
        const result = await scribeJobService.list();
        if (!stopped && accountScope() === owner && revision === jobRevision.current) {
          setJobs(result); setPollError(false);
        }
      } catch {
        if (!stopped && accountScope() === owner) setPollError(true);
      } finally { fetching = false; }
    };
    void tick();
    const timer = window.setInterval(tick, 3000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('auth-data-changed', tick);
    window.addEventListener('storage', tick);
    return () => {
      stopped = true; clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('auth-data-changed', tick);
      window.removeEventListener('storage', tick);
    };
  }, [location.key]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (visibleUploads.current.some(item => !item.error)) {
        event.preventDefault(); event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  const owned = scope.current === accountScope();
  const safeJobs = owned ? jobs : [];
  const safeUploads = owned ? uploads : [];
  const visibleJobs = safeJobs.filter(job => !dismissed.includes(job.job_id))
    .sort((a, b) => Number(jobIsActive(b)) - Number(jobIsActive(a)));
  const uploadVisits = Array.from(new Set(safeUploads.map(item => item.meta.sessionId)));
  const needsAttention = safeUploads.some(item => item.error) || visibleJobs.some(job => ['failed', 'completed_with_errors'].includes(job.status));
  const activeCount = safeUploads.length + visibleJobs.filter(jobIsActive).length;
  const complete = !needsAttention && activeCount === 0;
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!complete) setClosed(false);
  }, [complete]);
  const shown = environment.SCRIBE_BACKGROUND_ENABLED && !!accountScope() && (safeUploads.length > 0 || visibleJobs.length > 0) && !(complete && closed);
  useEffect(() => {
    if (shown) document.body.setAttribute('data-patient-analysis', 'shown');
    else document.body.removeAttribute('data-patient-analysis');
    return () => document.body.removeAttribute('data-patient-analysis');
  }, [shown]);
  return <ScribeQueueContext.Provider value={{ jobs: safeJobs, uploads: safeUploads, submit }}>
    {children}
    {shown &&
      <Paper component="section" aria-label="Patient analysis queue" elevation={6}
        sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: theme => theme.zIndex.snackbar - 1,
          width: complete && !expanded ? 'auto' : { xs: 'calc(100% - 32px)', sm: 360 }, maxWidth: 400,
          bgcolor: 'background.paper', border: 1, borderColor: needsAttention ? 'error.main' : 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {complete ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls="patient-analysis-details"
              sx={{ px: 2, py: 1, textAlign: 'left', flexGrow: 1, justifyContent: 'flex-start' }}>
              Patient analysis complete
            </Button>
            <IconButton size="small" aria-label="Close patient analysis" onClick={() => { setClosed(true); setExpanded(false); }} sx={{ mr: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Button fullWidth onClick={() => setExpanded(value => !value)} aria-expanded={expanded}
            aria-controls="patient-analysis-details" color={needsAttention ? 'error' : 'primary'}
            sx={{ justifyContent: 'space-between', px: 2, py: 1.5, textAlign: 'left' }}>
            <Box component="span">Patient analysis · {needsAttention ? 'Needs attention' : `${activeCount} in progress`}</Box>
            <Box component="span" sx={{ ml: 1 }}>{expanded ? 'Hide' : 'View'}</Box>
          </Button>
        )}
        <Collapse in={expanded}>
          <Stack id="patient-analysis-details" spacing={1} sx={{ p: 1.5, pt: 0, maxHeight: '45vh', overflowY: 'auto' }}>
            {uploadVisits.map(sessionId => {
              const items = safeUploads.filter(item => item.meta.sessionId === sessionId);
              const failed = items.filter(item => item.error);
              return <Alert key={sessionId} severity={failed.length ? 'error' : 'info'}>
                <Typography variant="body2" fontWeight={600}>Visit {sessionId} · {items.length} recording{items.length === 1 ? '' : 's'}</Typography>
                <Typography variant="body2">{failed.length ? 'Upload interrupted. Your recording is saved on this device. Keep this tab open and retry.' : 'Uploading. You can continue to another patient. Keep this tab open until the upload finishes.'}</Typography>
                {!!failed.length && <Button size="small" color="inherit" onClick={() => failed.forEach(item => submit(item.meta))}>Retry upload{failed.length > 1 ? 's' : ''}</Button>}
              </Alert>;
            })}
            {visibleJobs.map(job => <Alert key={job.job_id}
              severity={jobIsActive(job) ? 'info' : job.status === 'completed' ? 'success' : 'warning'}
              onClose={jobIsActive(job) ? undefined : () => setDismissed(ids => [...ids, job.job_id])}>
              <Typography variant="body2">Visit {job.session_id}: {job.status.replace(/_/g, ' ')}.</Typography>
              <Link to={`/doctor/sessions/view?sessionId=${job.session_id}`}>Open visit</Link>
              {['failed', 'completed_with_errors'].includes(job.status) && job.attempts < 3 &&
                <Button size="small" onClick={() => {
                  const owner = accountScope();
                  scribeJobService.retry(job.job_id).then(result => {
                    if (accountScope() === owner) {
                      jobRevision.current += 1;
                      setJobs(items => items.map(item => item.job_id === result.job_id ? result : item));
                    }
                  }).catch(() => setPollError(true));
                }}>Retry failed work</Button>}
            </Alert>)}
            {pollError && <Alert severity="warning">Progress is temporarily unavailable. Accepted work continues on the server.</Alert>}
          </Stack>
        </Collapse>
      </Paper>}
  </ScribeQueueContext.Provider>;
}

export { accountScope as scribeAccountScope };
